import { useCallback, useEffect, useMemo, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";

import { isAfter, isBefore, subHours } from "date-fns";
import { useLocation } from "react-router";

import { GuideStatus } from "@mapmycustomers/shared/enum/guides";
import { Guide } from "@mapmycustomers/shared/types/entity";
import User from "@mapmycustomers/shared/types/User";

import configService from "@app/config/ConfigService";
import Environment from "@app/enum/Environment";
import { isAnnouncementsStartupWaitOver } from "@app/store/announcements/selectors";
import { getDismissedGuidesInfo } from "@app/store/iam";
import { updateMetadata } from "@app/store/iam/actions";
import useGuides from "@app/util/contexts/useGuides";
import { parseApiDate } from "@app/util/parsers";

const thresholdDate = new Date("2025-11-25T00:00:00.000Z");

interface AutoRunningResult {
  handleHideConfirm?: (closeMode: "close" | "forever") => void;
  handleTourShow: () => void;
}

const useAutoRunning = (
  slug: Guide["frigadeFlowId"],
  currentUser: User,
  isOnPath: (pathname: string) => boolean
): AutoRunningResult => {
  const dismissedGuidesInfo = useSelector(getDismissedGuidesInfo);
  const startupWaitOver = useSelector(isAnnouncementsStartupWaitOver);
  const dispatch = useDispatch();
  const { getGuideStatus, showGuide, visibility } = useGuides();

  const location = useLocation();

  const isCompleted = getGuideStatus(slug)?.status === GuideStatus.COMPLETED;
  const isAnyOtherGuideVisible = Object.keys(visibility).some(
    (id) => id !== slug && visibility[id]
  );

  const shouldNagUser =
    !isCompleted &&
    !isAnyOtherGuideVisible &&
    isAfter(parseApiDate(currentUser.createdAt), thresholdDate);

  useEffect(() => {
    if (
      [Environment.DEVELOPMENT, Environment.STAGING].includes(configService.getCurrentEnvironment())
    ) {
      window.__mmc_reset_dismissed_statuses__ = () => {
        dispatch(updateMetadata.request({ dismissedGuides: {} }));
      };
    }
    return () => {
      delete window.__mmc_reset_dismissed_statuses__;
    };
  }, [dispatch]);

  const handleHideConfirm = useCallback(
    (closeMode: "close" | "forever") => {
      dispatch(
        updateMetadata.request({
          dismissedGuides: {
            ...(dismissedGuidesInfo ?? {}),
            [slug]: closeMode === "forever" ? "forever" : new Date().toISOString(),
          },
        })
      );
    },
    [dispatch, dismissedGuidesInfo, slug]
  );

  // When tour is shown, Checklist modal should hide, but
  // the useEffect below shows it again due to its logic.
  // Here we're setting a state to prevent it from showing
  // again. This will live until user reloads the page.
  // This must be a ref to avoid any race condition issues in
  // the useEffect that shows a guide.
  const preventFromShowing = useRef(false);
  const handleTourShow = useCallback(() => {
    preventFromShowing.current = true;
  }, []);

  const shouldShow = useMemo(() => {
    // show guide if
    // a) user signed up after a certain date
    // b) it has not been completed yet
    // c) user is on the `isOnPath` page
    // d) user temporarily dismissed it more than an hour ago or never
    // e) the first announcements fetch has settled or timed out, so a due modal usually shows first

    let shouldShow = shouldNagUser && startupWaitOver && isOnPath(location.pathname);
    const dismissed = dismissedGuidesInfo?.[slug];
    if (dismissed === "forever") {
      shouldShow = false;
    } else if (dismissed) {
      const dismissedOn = parseApiDate(dismissed);
      // only show again if dismissed 1+ hour ago to avoid
      // showing it too frequently annoying user
      shouldShow &&= isBefore(dismissedOn, subHours(Date.now(), 1));
    }

    return shouldShow;
  }, [shouldNagUser, startupWaitOver, isOnPath, location.pathname, dismissedGuidesInfo, slug]);

  // Refused while another guide or the announcements modal holds the screen; retried when it frees.
  useEffect(() => {
    if (shouldShow && !preventFromShowing.current) {
      showGuide(slug, { mode: "auto" });
    }
  }, [shouldShow, showGuide, slug]);

  return { handleHideConfirm: shouldNagUser ? handleHideConfirm : undefined, handleTourShow };
};

export default useAutoRunning;
